/**
 * Form Designer V1 model — FORM → CARDS → CONTROLS.
 *
 * A form holds presentation only. A database-bound control records the column
 * NAME it binds to and nothing else; the column's data type, required state
 * and primary-key state stay in PostgreSQL and arrive with the live catalog
 * (see lib/formDesignerApi.js). A FHIR semantic control additionally stores
 * `binding` (resourceType, fhirPath, fieldType, components) so a composite
 * such as Patient Name can bind to Patient.name without a fabricated column.
 * The two bindings coexist. The designer never keeps a second copy of a
 * field definition, so a schema change in Table Designer is visible here
 * immediately.
 *
 * Geometry is integer grid cells only — no pixel offsets, no z-order. The
 * form canvas is one 48 × 32 grid of SQUARE cells. Cards occupy rectangles on
 * that canvas (`col, row, width, height`) and move through the same Page
 * Designer helpers in lib/designerGrid.js. Controls sit on their own card's
 * grid of `card.columns` columns (`columns` is kept equal to the card's canvas
 * `width`) and `card.height - 1` rows, so a card's cells ARE canvas cells and
 * a control snaps to the same squares a card does. All geometry goes through
 * that shared grid contract.
 */

import {
  alignItemsBottom,
  alignItemsLeft,
  alignItemsRight,
  alignItemsTop,
  asInt,
  cellFromPoint,
  clampItem,
  findFirstAvailablePlacement,
  findItem,
  nudgeItemsCells,
  tryMoveItem,
  tryMoveItemsTo,
  tryResizeItem,
  tryUpdateItem,
} from "@/lib/designerGrid";
import { normalizeSemanticBinding, semanticBinding } from "@/lib/formDesignerSemantic";

export const GRID_COLUMNS = 48;
export const GRID_ROWS = 32;
export const GRID_MIN_COLUMNS = 1;
export const GRID_MAX_COLUMNS = 48;
export const GRID_MIN_ROWS = 6;
export const GRID_MAX_ROWS = 32;

/**
 * The cell is a SQUARE: its side is the canvas width divided by GRID_COLUMNS,
 * and the canvas keeps a 48 / 32 aspect ratio so the row height matches. No
 * pixel size is stored on a card or a control — the canvas measures itself and
 * a pointer delta is read back as whole cells.
 *
 * Below about this many pixels a cell stops being a usable drop target and a
 * two-row control can no longer show a caption over an input, so the canvas
 * keeps this as a floor and scrolls horizontally instead of shrinking further.
 */
export const MIN_CELL_PX = 21;
export const CARD_MIN_ROWS = 6;
export const CARD_MAX_ROWS = GRID_MAX_ROWS;
/** The title/drag band is one canvas row so the card box stays whole cells. */
export const CARD_HEADER_ROWS = 1;
export const NEW_CARD_PREFERRED_WIDTH = GRID_COLUMNS;
export const NEW_CARD_PREFERRED_HEIGHT = CARD_MIN_ROWS + CARD_HEADER_ROWS;

/** The one canvas a Form Designer card is checked against. */
export function formBounds(form) {
  return {
    columns: Math.min(GRID_MAX_COLUMNS, Math.max(GRID_MIN_COLUMNS, asInt(form?.grid?.columns, GRID_COLUMNS))),
    rows: Math.min(GRID_MAX_ROWS, Math.max(GRID_MIN_ROWS, asInt(form?.grid?.rows, GRID_ROWS))),
  };
}

/**
 * V1 laid a form out on 12 columns of 52px rows; V1.1 halved the cell in both
 * axes to 24 columns of 26px rows. V1.2 makes the cell square on a 48 × 32
 * canvas: the column halves again, while a row stays about the height it
 * already had.
 *
 * The unit a saved form was authored in is therefore part of its meaning, and
 * `grid.version` records it. An older form is scaled per axis when it is read
 * (see normalizeForm), which is the same approach the Page Designer takes for
 * its own earlier layouts.
 */
export const GRID_VERSION = 3;
/** Columns per stored column, and rows per stored row, by authored version. */
const LEGACY_COLUMN_SCALE = Object.freeze({ 1: 4, 2: 2 });
const LEGACY_ROW_SCALE = Object.freeze({ 1: 2, 2: 1 });

/**
 * Control types. `data` means the control *may* bind to a PostgreSQL column.
 * Toolbox placement always creates an unbound control. Dragging a database
 * Field onto a card creates the matching bound control. Label, Button, Tab,
 * Image, Line and Rectangle stay unbound — they are UI, not data.
 */
export const CONTROL_TYPES = Object.freeze([
  Object.freeze({ id: "label", label: "Label", data: false, toolbox: true, namePrefix: "Label", width: 12, height: 2 }),
  Object.freeze({ id: "text", label: "Text Box", data: true, toolbox: true, namePrefix: "TextBox", width: 12, height: 2 }),
  Object.freeze({ id: "button", label: "Button", data: false, toolbox: true, namePrefix: "Button", width: 8, height: 2 }),
  Object.freeze({ id: "checkbox", label: "Check Box", data: true, toolbox: true, namePrefix: "CheckBox", width: 8, height: 2 }),
  Object.freeze({ id: "radio", label: "Radio Button", data: true, toolbox: true, namePrefix: "Radio", width: 8, height: 2 }),
  Object.freeze({ id: "select", label: "Combo Box", data: true, toolbox: true, namePrefix: "ComboBox", width: 12, height: 2 }),
  Object.freeze({ id: "listbox", label: "List Box", data: true, toolbox: true, namePrefix: "ListBox", width: 12, height: 4 }),
  Object.freeze({ id: "tab", label: "Tab Control", data: false, toolbox: true, namePrefix: "TabControl", width: 24, height: 8 }),
  Object.freeze({ id: "image", label: "Image", data: false, toolbox: true, namePrefix: "Image", width: 12, height: 6 }),
  Object.freeze({ id: "line", label: "Line", data: false, toolbox: true, namePrefix: "Line", width: 16, height: 1 }),
  Object.freeze({ id: "rectangle", label: "Rectangle", data: false, toolbox: true, namePrefix: "Rectangle", width: 16, height: 4 }),
  Object.freeze({ id: "number", label: "Number", data: true, toolbox: false, namePrefix: "NumberBox", width: 12, height: 2 }),
  Object.freeze({ id: "date", label: "Date", data: true, toolbox: false, namePrefix: "DateBox", width: 12, height: 2 }),
  Object.freeze({ id: "textarea", label: "Textarea", data: true, toolbox: false, namePrefix: "TextArea", width: 24, height: 4 }),
]);

export const DATA_CONTROL_TYPES = CONTROL_TYPES.filter((type) => type.data);
export const STATIC_CONTROL_TYPES = CONTROL_TYPES.filter((type) => !type.data);
export const TOOLBOX_CONTROL_TYPES = CONTROL_TYPES.filter((type) => type.toolbox);

/** Palette default when the catalog does not send a suggestion. */
const CONTROL_TYPE_FOR_PG_TYPE = Object.freeze({
  boolean: "checkbox",
  date: "date",
  timestamp: "date",
  timestamptz: "date",
  integer: "number",
  bigint: "number",
  numeric: "number",
  jsonb: "textarea",
});

export const NO_CARD_MESSAGE = "Add a card before placing fields.";
export const NO_ROOM_MESSAGE = "No room left in this card. Resize a control or add another card.";
export const SELECT_TABLE_MESSAGE = "Select a primary table to see its fields.";

export function controlTypeOption(id) {
  return CONTROL_TYPES.find((type) => type.id === id) || CONTROL_TYPES[0];
}

export function isDataControl(controlType) {
  return controlTypeOption(controlType).data;
}

/** A bindable control can hold a field later; it does not have to start bound. */
export function isBindableControl(controlType) {
  return controlTypeOption(controlType).data;
}

/** The catalog's own suggestion wins; the local map is the offline fallback. */
export function controlTypeForField(field) {
  if (field?.controlType && CONTROL_TYPES.some((type) => type.id === field.controlType)) {
    return field.controlType;
  }
  if (field?.lookup?.kind === "static" || field?.lookup?.kind === "reference") return "select";
  return CONTROL_TYPE_FOR_PG_TYPE[String(field?.dataType || "").toLowerCase()] || "text";
}

export function humanizeField(name) {
  return String(name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * A new form has no primary table. Data Fields stay empty and Save stays
 * disabled until the user picks one — the first table in the catalog is never
 * assumed.
 */
export function emptyForm() {
  return {
    id: null,
    name: "New Form",
    description: "",
    primarySchema: "public",
    primaryTable: "",
    grid: { columns: GRID_COLUMNS, rows: GRID_ROWS, version: GRID_VERSION },
    cards: [],
    controls: [],
  };
}

function nextSequence(items, prefix) {
  const pattern = new RegExp(`^${prefix}-(\\d+)$`);
  let max = 0;
  (items || []).forEach((item) => {
    const match = pattern.exec(String(item?.id || ""));
    if (match) max = Math.max(max, Number.parseInt(match[1], 10));
  });
  return max + 1;
}

function nextControlName(controls, option) {
  const prefix = option?.namePrefix || "Control";
  const pattern = new RegExp(`^${prefix}(\\d+)$`);
  let max = 0;
  (controls || []).forEach((item) => {
    const match = pattern.exec(String(item?.name || ""));
    if (match) max = Math.max(max, Number.parseInt(match[1], 10));
  });
  return `${prefix}${max + 1}`;
}

export function controlGridRows(card) {
  return Math.max(1, cardHeight(card) - CARD_HEADER_ROWS);
}

export function cardBounds(card) {
  return {
    columns: Math.max(1, asInt(card?.width ?? card?.columns, GRID_COLUMNS)),
    rows: controlGridRows(card),
  };
}

function cardWidth(card, fallback = GRID_COLUMNS) {
  return clampColumns(asInt(card?.width ?? card?.columns, fallback));
}

function cardHeight(card, fallback = NEW_CARD_PREFERRED_HEIGHT) {
  return Math.min(CARD_MAX_ROWS, Math.max(NEW_CARD_PREFERRED_HEIGHT, asInt(card?.height, fallback)));
}

/** Canvas geometry for a card. `columns` stays equal to canvas `width`. */
export function withCardGeometry(card, fallbackColumns = GRID_COLUMNS) {
  const width = cardWidth(card, fallbackColumns);
  const height = cardHeight(card);
  return {
    ...card,
    columns: width,
    width,
    height,
    col: Math.max(1, asInt(card?.col, 1)),
    row: Math.max(1, asInt(card?.row, 1)),
  };
}

export function controlsForCard(form, cardId) {
  return (form?.controls || []).filter((control) => control.cardId === cardId);
}

/**
 * Rows a card shows: its own canvas height less the title band. Derived, so it
 * is not persisted, and exactly the rows the card occupies on the canvas — a
 * control therefore snaps to the same square cells the card itself does.
 */
export function cardRows(form, cardId) {
  return controlGridRows(findCard(form, cardId));
}

function defaultCardTitle(number) {
  return number === 1 ? "New Card" : `New Card ${number}`;
}

export function addCard(form, title = "") {
  const number = nextSequence(form.cards, "card");
  const bounds = formBounds(form);
  const placed = (form.cards || []).map((card) => withCardGeometry(card, bounds.columns));
  const placement = findFirstAvailablePlacement(
    placed,
    bounds,
    Math.min(NEW_CARD_PREFERRED_WIDTH, bounds.columns),
    NEW_CARD_PREFERRED_HEIGHT,
  );
  if (!placement) return null;
  const card = {
    id: `card-${number}`,
    title: String(title || "").trim() || defaultCardTitle(number),
    columns: placement.width,
    collapsible: false,
    ...placement,
  };
  return { form: { ...form, cards: [...placed, card] }, card };
}

export function removeCard(form, cardId) {
  if (!findItemById(form.cards, cardId)) return null;
  return {
    ...form,
    cards: (form.cards || []).filter((card) => card.id !== cardId),
    controls: (form.controls || []).filter((control) => control.cardId !== cardId),
  };
}

function findItemById(items, id) {
  return (items || []).find((item) => item.id === id) || null;
}

export function findCard(form, cardId) {
  return findItemById(form?.cards, cardId);
}

export function findControl(form, controlId) {
  return findItemById(form?.controls, controlId);
}

/**
 * Narrowing a card's columns has to take its controls with it, otherwise a
 * control would be left hanging off the right edge of its own card.
 */
function applyCardLayout(form, cards) {
  if (!cards) return null;
  const nextCards = cards.map((card) => withCardGeometry(card, formBounds(form).columns));
  const controls = (form.controls || []).map((control) => {
    const card = nextCards.find((item) => item.id === control.cardId);
    return card ? clampItem(control, cardBounds(card)) : control;
  });
  return { ...form, cards: nextCards, controls };
}

export function updateCard(form, cardId, patch) {
  const card = findCard(form, cardId);
  if (!card) return null;
  const current = withCardGeometry(card, formBounds(form).columns);
  const next = { ...current, ...patch };
  if (patch.columns != null || patch.width != null) {
    const columns = Number.parseInt(patch.columns ?? patch.width, 10);
    const width = clampColumns(Number.isFinite(columns) ? columns : current.columns);
    next.columns = width;
    next.width = width;
  }
  if (patch.height != null) {
    next.height = Math.min(CARD_MAX_ROWS, Math.max(NEW_CARD_PREFERRED_HEIGHT, asInt(patch.height, current.height)));
  }
  if (patch.col != null || patch.row != null) {
    const moved = tryMoveItem(form.cards.map((item) => withCardGeometry(item, formBounds(form).columns)), cardId, next.col, next.row, {
      bounds: formBounds(form),
    });
    if (!moved) return null;
    const resized = next.width !== current.width || next.height !== current.height
      ? tryResizeItem(moved, cardId, next.width, next.height, { bounds: formBounds(form) })
      : moved;
    if (!resized) return null;
    const styled = resized.map((item) =>
      item.id === cardId ? { ...item, ...next, ...findItem(resized, cardId) } : item,
    );
    return applyCardLayout(form, styled);
  }
  if (next.width !== current.width || next.height !== current.height) {
    const cards = (form.cards || []).map((item) => withCardGeometry(item, formBounds(form).columns));
    const resized = tryResizeItem(cards, cardId, next.width, next.height, { bounds: formBounds(form) });
    if (!resized) return null;
    const styled = resized.map((item) =>
      item.id === cardId ? { ...item, ...next, width: item.width, height: item.height, columns: item.width } : item,
    );
    return applyCardLayout(form, styled);
  }
  return applyCardLayout(
    form,
    (form.cards || []).map((item) => (item.id === cardId ? next : withCardGeometry(item, formBounds(form).columns))),
  );
}

function cardsOnCanvas(form) {
  return (form.cards || []).map((card) => withCardGeometry(card, formBounds(form).columns));
}

/** Moves a card to a whole canvas cell, sliding up to neighbours. */
export function moveCardTo(form, cardId, col, row) {
  const next = tryMoveItem(cardsOnCanvas(form), cardId, col, row, { bounds: formBounds(form) });
  return next ? { ...form, cards: next } : null;
}

/** Resizes a card in whole canvas cells. Controls stay inside the new size. */
export function resizeCardTo(form, cardId, width, height) {
  const next = tryResizeItem(cardsOnCanvas(form), cardId, width, height, { bounds: formBounds(form) });
  return next ? applyCardLayout(form, next) : null;
}

/** Page Designer group-drag: every listed card moves, or none do. */
export function moveCardsTo(form, moves) {
  const next = tryMoveItemsTo(cardsOnCanvas(form), moves, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

/** Page Designer arrow-key nudge, including the legacy Move Up/Down buttons. */
export function moveCard(form, cardId, delta) {
  const next = nudgeItemsCells(cardsOnCanvas(form), [cardId], 0, delta, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

export function nudgeCards(form, ids, dCol, dRow) {
  const next = nudgeItemsCells(cardsOnCanvas(form), ids, dCol, dRow, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

export function alignCardsLeft(form, ids) {
  const next = alignItemsLeft(cardsOnCanvas(form), ids, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

export function alignCardsRight(form, ids) {
  const next = alignItemsRight(cardsOnCanvas(form), ids, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

export function alignCardsTop(form, ids) {
  const next = alignItemsTop(cardsOnCanvas(form), ids, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

export function alignCardsBottom(form, ids) {
  const next = alignItemsBottom(cardsOnCanvas(form), ids, formBounds(form));
  return next ? { ...form, cards: next } : null;
}

function growCardToFit(form, cardId, row, height) {
  const card = findCard(form, cardId);
  if (!card) return form;
  const neededControls = Math.min(
    CARD_MAX_ROWS - CARD_HEADER_ROWS,
    Math.max(CARD_MIN_ROWS, row + height - 1),
  );
  const needed = neededControls + CARD_HEADER_ROWS;
  if (needed <= cardHeight(card)) return form;
  return resizeCardTo(form, cardId, cardWidth(card), needed) || form;
}

function placeControl(form, cardId, width, height, at) {
  const grown = at ? growCardToFit(form, cardId, asInt(at.row, 1), height) : form;
  const card = findCard(grown, cardId);
  if (!card) return { form, placement: null };
  const bounds = cardBounds(card);
  const peers = controlsForCard(grown, cardId);
  if (at) {
    const candidate = clampItem({ id: "__new__", ...at, width, height }, bounds);
    const clear = !peers.some(
      (peer) =>
        candidate.col < peer.col + peer.width &&
        candidate.col + candidate.width > peer.col &&
        candidate.row < peer.row + peer.height &&
        candidate.row + candidate.height > peer.row,
    );
    if (clear) {
      return {
        form: grown,
        placement: { col: candidate.col, row: candidate.row, width: candidate.width, height: candidate.height },
      };
    }
  }
  return { form: grown, placement: findFirstAvailablePlacement(peers, bounds, width, height) };
}

function bindingFromField(field) {
  const semantic = field?.semantic || (field?.fhirPath && field?.resourceType ? field : null);
  return semantic ? semanticBinding(semantic) : null;
}

/**
 * Binds a field into a card. `field` may be a live catalog column or a FHIR
 * semantic field (or both). A composite such as Patient Name has a FHIR path
 * and no PostgreSQL column; a simple field such as MRN keeps its column name
 * so the existing database-backed binding still works.
 *
 * A caller that has already worked out how the field reads (the Fields panel
 * does, through lib/formDesignerFields.js) can pass `displayLabel`, so a
 * control dropped from `Patient Name` is captioned the same way the field list
 * named it. Without one the caption falls back to the column name or the
 * semantic label.
 */
export function addFieldControl(form, cardId, field, at = null) {
  const binding = bindingFromField(field);
  const columnName = String(field?.name || field?.databaseColumn || binding?.databaseColumn || "").trim();
  if (!columnName && !binding) return null;
  const controlType = controlTypeForField(field);
  const option = controlTypeOption(controlType);
  const { form: nextForm, placement } = placeControl(form, cardId, option.width, option.height, at);
  if (!placement) return null;
  const control = {
    id: `control-${nextSequence(nextForm.controls, "control")}`,
    name: nextControlName(nextForm.controls, option),
    cardId,
    controlType,
    field: columnName,
    label:
      String(field.displayLabel || "").trim()
      || binding?.label
      || humanizeField(columnName),
    placeholder: "",
    helpText: "",
    required: Boolean(field.required || field.primaryKey),
    readOnly: Boolean(field.primaryKey),
    ...placement,
  };
  if (binding) control.binding = binding;
  const withControl = { ...nextForm, controls: [...(nextForm.controls || []), control] };
  const fitted = growCardToFit(withControl, cardId, control.row, control.height);
  return { form: fitted, control: findControl(fitted, control.id) || control };
}

export function addStaticControl(form, cardId, controlType, at = null) {
  const option = CONTROL_TYPES.find((type) => type.id === controlType);
  if (!option) return null;
  const { form: nextForm, placement } = placeControl(form, cardId, option.width, option.height, at);
  if (!placement) return null;
  const control = {
    id: `control-${nextSequence(nextForm.controls, "control")}`,
    name: nextControlName(nextForm.controls, option),
    cardId,
    controlType: option.id,
    field: "",
    label: option.label,
    placeholder: "",
    helpText: "",
    required: false,
    readOnly: false,
    ...placement,
  };
  const withControl = { ...nextForm, controls: [...(nextForm.controls || []), control] };
  const fitted = growCardToFit(withControl, cardId, control.row, control.height);
  return { form: fitted, control: findControl(fitted, control.id) || control };
}

export function removeControl(form, controlId) {
  if (!findControl(form, controlId)) return null;
  return { ...form, controls: (form.controls || []).filter((control) => control.id !== controlId) };
}

/**
 * Property edits. A bound control may change between bindable types; it
 * cannot become a Label, Button or other unbound-only type while it still
 * carries a binding. An unbound Text Box (or any bindable type) may later
 * receive a Bound Field. Binding is refused on inherently unbound types.
 */
export function updateControl(form, controlId, patch) {
  const control = findControl(form, controlId);
  if (!control) return null;
  const next = { ...control };
  if (patch.name != null) next.name = String(patch.name).trim();
  if (patch.label != null) next.label = patch.label;
  if (patch.placeholder != null) next.placeholder = patch.placeholder;
  if (patch.helpText != null) next.helpText = patch.helpText;
  if (patch.required != null) next.required = Boolean(patch.required);
  if (patch.readOnly != null) next.readOnly = Boolean(patch.readOnly);
  if (patch.controlType) {
    const option = CONTROL_TYPES.find((type) => type.id === patch.controlType);
    if (!option) return null;
    if (control.field && !option.data) return null;
    next.controlType = option.id;
  }
  if (patch.field !== undefined) {
    const field = String(patch.field || "").trim();
    if (field && !isBindableControl(next.controlType)) return null;
    next.field = field;
    if (!field && patch.binding === undefined) next.binding = undefined;
  }
  if (patch.binding !== undefined) {
    next.binding = normalizeSemanticBinding(patch.binding) || undefined;
  }
  return { ...form, controls: (form.controls || []).map((item) => (item.id === controlId ? next : item)) };
}

function cardScopedUpdate(form, controlId, apply) {
  const control = findControl(form, controlId);
  if (!control) return null;
  const card = findCard(form, control.cardId);
  if (!card) return null;
  const peers = controlsForCard(form, control.cardId);
  const next = apply(form.controls, { bounds: cardBounds(card), peers });
  if (!next) return null;
  return { ...form, controls: next };
}

/** Moves a control by whole cells inside its own card, sliding up to neighbours. */
export function moveControl(form, controlId, col, row) {
  return cardScopedUpdate(form, controlId, (controls, options) =>
    tryMoveItem(controls, controlId, col, row, options),
  );
}

/** Resizes a control by whole cells — the resize counterpart of moveControl. */
export function resizeControl(form, controlId, width, height) {
  return cardScopedUpdate(form, controlId, (controls, options) =>
    tryResizeItem(controls, controlId, width, height, options),
  );
}

/** Reassigns a control to another card, placing it in the first free cells there. */
export function reassignControl(form, controlId, cardId) {
  const control = findControl(form, controlId);
  const card = findCard(form, cardId);
  if (!control || !card || control.cardId === cardId) return null;
  const placement = findFirstAvailablePlacement(
    controlsForCard(form, cardId),
    cardBounds(card),
    control.width,
    control.height,
  );
  if (!placement) return null;
  return {
    ...form,
    controls: (form.controls || []).map((item) =>
      item.id === controlId ? { ...item, cardId, ...placement } : item,
    ),
  };
}

export function controlBinding(form, control) {
  if (control?.binding?.fhirPath) return control.binding.fhirPath;
  if (!control?.field) return "";
  return `${form?.primaryTable || ""}.${control.field}`;
}

export function boundFieldNames(form) {
  const names = [];
  (form?.controls || []).forEach((control) => {
    if (control.field && !names.includes(control.field)) names.push(control.field);
  });
  return names;
}

export function isFieldBound(form, name) {
  return (form?.controls || []).some((control) => control.field === name);
}

/** Cell under the pointer for a drop into a card's grid. */
export function dropCell(rect, card, rows, clientX, clientY) {
  return cellFromPoint(rect, { columns: cardBounds(card).columns, rows }, clientX, clientY);
}

/** 1-based cell index onto a grid `scale` times finer: cell 2 of 12 → cell 3 of 24. */
function scaleOrigin(value, scale) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? (n - 1) * scale + 1 : value;
}

/** A span is a count of cells, so it scales directly. */
function scaleSpan(value, scale) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n * scale : value;
}

function scaleColumns(value, scale, fallback) {
  const n = Number.parseInt(value, 10);
  return clampColumns(Number.isFinite(n) ? n * scale : fallback);
}

function clampColumns(value) {
  return Math.min(GRID_MAX_COLUMNS, Math.max(GRID_MIN_COLUMNS, value));
}

/**
 * Repairs a saved or server-returned form. Controls whose card is gone, or
 * whose binding is no longer a bindable column, are dropped rather than
 * rebound — a control pointing at the wrong column is worse than a missing
 * one the user can put back.
 *
 * A form saved before the grid was refined carries no `grid.version`, and its
 * cells are twice the current size. Scaling it here means one stored geometry
 * always renders at the size it was designed at; the scaled form is what the
 * next Save persists, at the current version.
 */
export function normalizeForm(raw, bindableNames = null) {
  const base = emptyForm();
  const version = Number.parseInt(raw?.grid?.version, 10) || 1;
  // The cell changed shape, not just size: the column has halved twice while a
  // row stayed about as tall as it was, so each axis carries its own scale.
  const colScale = LEGACY_COLUMN_SCALE[version] || 1;
  const rowScale = LEGACY_ROW_SCALE[version] || 1;
  const columns = scaleColumns(raw?.grid?.columns, colScale, GRID_COLUMNS);
  const rows = scaleSpan(raw?.grid?.rows, rowScale) || GRID_ROWS;
  const bounds = {
    columns,
    rows: Math.min(GRID_MAX_ROWS, Math.max(GRID_MIN_ROWS, asInt(rows, GRID_ROWS))),
  };
  const draftCards = [];
  const seenCards = new Set();
  (raw?.cards || []).forEach((card, index) => {
    const id = String(card?.id || "").trim() || `card-${index + 1}`;
    if (seenCards.has(id)) return;
    seenCards.add(id);
    const width = scaleColumns(card?.width ?? card?.columns, colScale, columns);
    const height = Math.min(
      CARD_MAX_ROWS,
      Math.max(NEW_CARD_PREFERRED_HEIGHT, scaleSpan(card?.height, rowScale) || NEW_CARD_PREFERRED_HEIGHT),
    );
    draftCards.push({
      id,
      title: String(card?.title || "").trim() || `Card ${index + 1}`,
      columns: width,
      collapsible: Boolean(card?.collapsible),
      width,
      height,
      col: card?.col != null ? scaleOrigin(card.col, colScale) : null,
      row: card?.row != null ? scaleOrigin(card.row, rowScale) : null,
    });
  });
  const allowed = bindableNames ? new Set(bindableNames) : null;
  const byCard = new Map(draftCards.map((card) => [card.id, []]));
  const seenControls = new Set();
  (raw?.controls || []).forEach((control, index) => {
    const id = String(control?.id || "").trim() || `control-${index + 1}`;
    if (seenControls.has(id) || !byCard.has(control?.cardId)) return;
    const field = String(control?.field || "").trim();
    const binding = normalizeSemanticBinding(control?.binding || control?.semanticFieldBinding);
    if (field && allowed && !allowed.has(field)) return;
    const option = CONTROL_TYPES.find((type) => type.id === control?.controlType) || controlTypeOption(control?.controlType);
    if (field && !option.data) return;
    seenControls.add(id);
    const card = draftCards.find((item) => item.id === control.cardId);
    const controlBounds = { columns: cardWidth(card, bounds.columns), rows: CARD_MAX_ROWS };
    const peers = byCard.get(card.id);
    const clamped = clampItem(
      {
        id,
        cardId: card.id,
        controlType: option.id,
        name: String(control?.name || "").trim() || nextControlName([...byCard.values()].flat(), option),
        field,
        label: String(control?.label || "").trim() || binding?.label || (field ? humanizeField(field) : option.label),
        placeholder: String(control?.placeholder || ""),
        helpText: String(control?.helpText || ""),
        required: Boolean(control?.required),
        readOnly: Boolean(control?.readOnly),
        col: scaleOrigin(control?.col, colScale),
        row: scaleOrigin(control?.row, rowScale),
        width: scaleSpan(control?.width, colScale),
        height: scaleSpan(control?.height, rowScale),
        ...(binding ? { binding } : {}),
      },
      controlBounds,
    );
    const placed = tryUpdateItem([...peers, clamped], id, {}, { bounds: controlBounds, peers })
      ? clamped
      : { ...clamped, ...(findFirstAvailablePlacement(peers, controlBounds, clamped.width, clamped.height) || {}) };
    peers.push(placed);
  });

  const sizedCards = draftCards.map((card) => {
    const used = (byCard.get(card.id) || []).reduce(
      (max, control) => Math.max(max, control.row + control.height - 1),
      0,
    );
    return {
      ...card,
      height: Math.min(
        CARD_MAX_ROWS,
        Math.max(card.height, NEW_CARD_PREFERRED_HEIGHT, used + CARD_HEADER_ROWS),
      ),
    };
  });
  const cards = [];
  sizedCards.forEach((card) => {
    const geom = withCardGeometry(
      {
        ...card,
        col: card.col ?? 1,
        row: card.row ?? 1,
      },
      bounds.columns,
    );
    const saved = card.col != null && card.row != null;
    const clamped = clampItem(geom, bounds);
    let placed = clamped;
    if (saved) {
      const accepted = tryUpdateItem([...cards, clamped], clamped.id, {}, { bounds, peers: cards });
      if (!accepted) {
        placed = { ...clamped, ...(findFirstAvailablePlacement(cards, bounds, clamped.width, clamped.height) || {}) };
      }
    } else {
      placed = {
        ...clamped,
        ...(findFirstAvailablePlacement(cards, bounds, clamped.width, clamped.height) || { col: 1, row: 1 }),
      };
    }
    cards.push(withCardGeometry(placed, bounds.columns));
  });

  // A card that could not grow to its full stored height (the canvas is only
  // GRID_ROWS tall) must still contain its controls, so every control is
  // finally checked against the card it actually ended up with.
  const controls = [];
  cards.forEach((card) => {
    const controlBounds = cardBounds(card);
    const placed = [];
    (byCard.get(card.id) || []).forEach((control) => {
      const clamped = clampItem(control, controlBounds);
      const fits = tryUpdateItem([...placed, clamped], clamped.id, {}, { bounds: controlBounds, peers: placed });
      placed.push(
        fits
          ? clamped
          : {
              ...clamped,
              ...(findFirstAvailablePlacement(placed, controlBounds, clamped.width, clamped.height) || {}),
            },
      );
    });
    controls.push(...placed);
  });

  return {
    ...base,
    id: raw?.id || null,
    name: String(raw?.name || "").trim() || base.name,
    description: String(raw?.description || ""),
    primarySchema: String(raw?.primarySchema || base.primarySchema),
    primaryTable: String(raw?.primaryTable || ""),
    grid: { columns, rows: bounds.rows, version: GRID_VERSION },
    cards,
    controls,
  };
}

export function toSavePayload(form) {
  return {
    id: form.id || null,
    name: form.name,
    description: form.description || "",
    primarySchema: form.primarySchema || "public",
    primaryTable: form.primaryTable,
    grid: {
      columns: form.grid?.columns || GRID_COLUMNS,
      rows: form.grid?.rows || GRID_ROWS,
      version: GRID_VERSION,
    },
    cards: (form.cards || []).map((card) => {
      const geom = withCardGeometry(card, form.grid?.columns || GRID_COLUMNS);
      return {
        id: geom.id,
        title: geom.title,
        columns: geom.columns,
        collapsible: Boolean(geom.collapsible),
        col: geom.col,
        row: geom.row,
        width: geom.width,
        height: geom.height,
      };
    }),
    controls: (form.controls || []).map((control) => {
      const binding = normalizeSemanticBinding(control.binding);
      const payload = {
        id: control.id,
        name: control.name || "",
        cardId: control.cardId,
        controlType: control.controlType,
        field: control.field || "",
        label: control.label || "",
        placeholder: control.placeholder || "",
        helpText: control.helpText || "",
        required: Boolean(control.required),
        readOnly: Boolean(control.readOnly),
        col: control.col,
        row: control.row,
        width: control.width,
        height: control.height,
      };
      if (binding) payload.binding = binding;
      return payload;
    }),
  };
}

export { findItem };
